import styled from '@emotion/styled';
import {
  Breadcrumb,
  Button,
  Drawer,
  Input,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaArrowLeft, FaHome, FaPen, FaPlus, FaUser } from 'react-icons/fa';
import { FiRefreshCcw } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { PulseLoader } from 'react-spinners';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { ProfilEntity } from '../../../../entities/profil.entity';
import { UserEntity } from '../../../../entities/utilisateur.entity';
import { ROUTES } from '../../../../routes';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { Layout } from '../../../shared/Layout';
import { ProfilForm } from '../components/ProfilForm';
import { SideBar } from '../components/SidebarItems';
import { UserForm } from '../components/UserForm';
import { getUtilisateursProfils } from '../network';

const Container = styled.div`
  height: calc(100vh - 30px);
  background-color: white;
  display: flex;

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  .style-row {
    &:hover > td {
      background-color: #eee !important;
      cursor: pointer;
    }
  }

  .link {
    text-decoration: underline;
    cursor: pointer;
  }

  > .content {
    padding: 50px;
    width: 85%;
    background-color: #e8edea;
  }
`;

export const UserAgencePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showProfilDrawer, setShowProfilDrawer] = useState(false);
  const [showUpdateUserDrawer, setShowUpdateUserDrawer] = useState(false);
  const agence = (useLocation().state as any)?.agence;
  const [users, setUsers] = useState<UserEntity[]>(
    (useLocation().state as any)?.users,
  );
  const [userProfiles, setUserProfiles] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserEntity>();
  const [selectedUserPro, setSelectedUserPro] = useState<ProfilEntity[]>([]);
  const [filterUsers, setFilterUsers] = useState<UserEntity[]>(
    (useLocation().state as any)?.users,
  );
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  const history = useHistory();

  const fetchData = () => {
    setIsLoading(true);
    getUtilisateursProfils(connectedUser.id).then(({ data }) => {
      setUserProfiles(data);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (agence === undefined || users === undefined) {
      history.push(ROUTES.PANEL.ADMIN.PARTENAIRE);
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setColor = (code: string) => {
    switch (code) {
      case 'admin':
        return '#f50';
      case 'resp_commercial':
        return '#3c096c';
      case 'commercial':
        return '#f9a825';
      case 'responsable':
        return '#108ee9';
      case 'chef_agence':
        return '#8d6e63';
      case 'gestionnaire':
        return '#5f0f40';
      case 'sadmin':
        return '#fb8b24';
    }
  };

  const COLUMNS = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      flex: 1,
    },
    {
      title: 'Prénom',
      dataIndex: 'prenom',
      flex: 1,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      flex: 1,
    },
    {
      title: 'Adresse',
      dataIndex: 'adresse',
      flex: 1,
    },
    {
      title: 'Contact',
      dataIndex: 'mobile',
      flex: 1,
    },
    {
      title: 'Date création',
      dataIndex: 'created_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
      flex: 1,
    },
    {
      title: 'Profils',
      render: (cell: any, row: UserEntity) => {
        return (
          <Space>
            {userProfiles
              .find((uPro) => row.id === uPro.id)
              ?.profils.map((pro: any) => (
                <Tag key={pro.id} color={setColor(pro.code_profil)}>
                  {pro.nom}
                </Tag>
              )) || <Tag>--</Tag>}
          </Space>
        );
      },
      flex: 2,
    },
    {
      title: 'Action',
      render: (cell: any, row: UserEntity) => (
        <Space>
          <Button
            icon={<FaPen style={{ color: 'black', marginBottom: -1 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
              borderColor: PRIMARY,
            }}
            onClick={() => {
              setSelectedUser(row);
              setShowUpdateUserDrawer(true);
            }}
          />
          <Button
            icon={<FaUser style={{ color: 'black', marginBottom: -1 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
              borderColor: PRIMARY,
            }}
            onClick={() => {
              setSelectedUserPro(
                userProfiles.find((uPro) => row.id === uPro.id).profils,
              );
              setSelectedUser(row);
              setShowProfilDrawer(true);
            }}
          />
        </Space>
      ),
      flex: 1,
    },
  ];

  return (
    <Layout hideYellowScrollInfo>
      <Container>
        <SideBar defaultOpenKeys={['m1']} current='1.1' />{' '}
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <Breadcrumb separator='>' className='bread-crumb'>
            <Breadcrumb.Item>
              <FaHome style={{ marginRight: 5 }} />
              <span>Administration</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item
              onClick={() => history.push(ROUTES.PANEL.ADMIN.PARTENAIRE)}
            >
              <span className='link'>Partenaires</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Agences</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Utilisateurs</span>
            </Breadcrumb.Item>
          </Breadcrumb>

          <div style={{ marginTop: 20 }}>
            <div
              style={{
                borderRadius: '50%',
                padding: 5,
                backgroundColor: PRIMARY,
                width: 40,
                height: 40,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                marginBottom: 5,
              }}
              onClick={() => history.goBack()}
            >
              <FaArrowLeft size={20} />
            </div>

            <h2>Liste des Utilisateurs Agence: {agence?.nom}</h2>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 5,
              }}
            >
              <Space>
                <Input
                  placeholder='Search...'
                  style={{ width: 250 }}
                  onChange={(e) => {
                    setFilterUsers(
                      users.filter(
                        (age) =>
                          age.nom
                            .toLowerCase()
                            .includes(e.target.value.toLowerCase()) ||
                          age.prenom
                            .toLowerCase()
                            .includes(e.target.value.toLowerCase()) ||
                          age.adresse
                            .toLowerCase()
                            .includes(e.target.value.toLowerCase()),
                      ),
                    );
                  }}
                />
                <Tooltip title='Rafraichir' placement='top' color={YELLOW}>
                  <span style={{ cursor: 'pointer' }} onClick={fetchData}>
                    <FiRefreshCcw color='black' />
                  </span>
                </Tooltip>
                <PulseLoader
                  size={5}
                  margin={2}
                  color={YELLOW}
                  loading={isLoading}
                />
              </Space>

              <Button
                icon={<FaPlus style={{ marginBottom: -2, marginRight: 5 }} />}
                className='butt'
                style={{ borderRadius: 5 }}
                onClick={() => setShowDrawer(true)}
              >
                Nouvel Utilisateur
              </Button>
            </div>

            <Table
              columns={COLUMNS}
              dataSource={filterUsers}
              rowClassName='style-row'
              rowKey='id'
              size='small'
              loading={isLoading}
              style={{ zoom: 0.9 }}
              pagination={false}
            />
          </div>
        </div>
        <Drawer
          title='Ajouter un utilisateur'
          placement='right'
          width={400}
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <UserForm
            agence={agence}
            setShowDrawer={setShowDrawer}
            getNewUser={(user) => {
              setUsers([...users, user]);
              setFilterUsers([...filterUsers, user]);
            }}
          />
        </Drawer>
        <Drawer
          title='Gestion des profils'
          placement='right'
          width={400}
          onClose={() => setShowProfilDrawer(false)}
          open={showProfilDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <ProfilForm user={selectedUser} userProfils={selectedUserPro} />
        </Drawer>
        <Drawer
          title="Mettre à jour l'utilisateur"
          placement='right'
          width={400}
          onClose={() => setShowUpdateUserDrawer(false)}
          open={showUpdateUserDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <UserForm
            userToUpdate={selectedUser}
            agence={agence}
            setShowDrawer={setShowUpdateUserDrawer}
            getNewUser={(user) => {}}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};
